import { useCallback, useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useMutation } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { t } from 'i18next'

import { FeedbackContext } from '~/components/feedback'
import useModal from '~/hooks/useModal'
import { patch } from '~/modules/retailMedia/services/grocery/campaign'

/**
 * Limite imposto pelo armazenamento. O backend recusa o excedente em vez de
 * truncar, então a tela valida antes de gastar uma ida.
 */
export const NOTE_MAX_LENGTH = 255

type DecisionBody = {
  status: CampaignStatus
  action: CampaignActions
  note?: string
}

/**
 * Os dois 403 do fluxo se distinguem **pela forma do corpo**, não por código de
 * erro: o do domínio traz `name` e `messages`; o do middleware de tipo de conta
 * traz `error`. A exceção é o 409 de mídias não enviadas, que o domínio nomeia
 * (`ASSETS_NOT_INTEGRATED`) para não se confundir com o 409 de decisão alheia.
 */
type ReviewErrorData = {
  name?: string
  messages?: string[]
  error?: string
}

type Params = {
  campaignId?: string
  /** Para onde a decisão leva. A fila é a fonte de verdade do que sobrou. */
  queuePath: string
}

const prefix = 'rm:offsiteAdvertiserReview.detail'

const ASSETS_NOT_INTEGRATED = 'OFFSITE_ASSETS_NOT_INTEGRATED'

export const useOffsiteReviewDecision = ({ campaignId, queuePath }: Params) => {
  const navigate = useNavigate()

  const { toast } = useContext(FeedbackContext)

  const [Modal, showConfirmationModal, toggleConfirmationModal, setIsShowing] =
    useModal()

  const [contentModal, setContentModal] = useState<ContentModal | null>(null)

  /**
   * Preenchido quando o domínio recusa a decisão. Vira a fonte da vez sobre a
   * segregação: `can_approve` é conveniência de interface e pode vir ausente,
   * mas um 403 do domínio é a regra falando.
   */
  const [domainBlockMessage, setDomainBlockMessage] = useState<string | null>(
    null
  )

  const { mutate, isPending } = useMutation({
    mutationFn: (body: DecisionBody) => patch({ id: campaignId, body }),
    onSuccess: (_data, body) => {
      setIsShowing(false)

      toast.success(
        body.status === 'rejected'
          ? t(`${prefix}.feedback.rejectSuccess`)
          : t(`${prefix}.feedback.approveSuccess`)
      )

      navigate(queuePath)
    },
    onError: (error: AxiosError<ReviewErrorData>, body) => {
      const status = error.response?.status
      const data = error.response?.data
      const fromBackend = data?.messages?.join(' ')

      // 403 do domínio: ou quem pede criou a campanha, ou ela não é da conta.
      // As duas respostas têm a mesma forma, então a tela não afirma qual foi —
      // mostra o que o domínio disse e retira a ação.
      if (status === 403 && (data?.name || data?.messages)) {
        const message = fromBackend || t(`${prefix}.feedback.blocked`)

        setDomainBlockMessage(message)
        setIsShowing(false)
        toast.critical(message)

        return
      }

      if (status === 403) {
        setIsShowing(false)
        toast.critical(data?.error || t(`${prefix}.feedback.accountNotAllowed`))

        return
      }

      // Mídias ainda não enviadas ao canal: ninguém decidiu, a campanha só não
      // pode ser aprovada agora. Fica na tela; voltar à fila esconderia isso.
      if (status === 409 && data?.name === ASSETS_NOT_INTEGRATED) {
        setIsShowing(false)
        toast.critical(t(`${prefix}.feedback.assetsNotSent`))

        return
      }

      // Transição inválida: alguém decidiu antes. O estado desta tela está
      // provadamente velho, e a fila é quem sabe o que ainda espera revisão.
      if (status === 409) {
        setIsShowing(false)
        toast.critical(t(`${prefix}.feedback.alreadyDecided`))
        navigate(queuePath)

        return
      }

      // Motivo recusado e falha de despacho mantêm o modal aberto: nos dois
      // casos nada foi persistido e tentar de novo é o caminho.
      if (status === 422) {
        toast.critical(fromBackend || t(`${prefix}.feedback.invalidNote`))

        return
      }

      /* Só a aprovação despacha para o canal. Dizer a quem rejeitou que a
         campanha não foi enviada descreve um passo que a rejeição não tem. */
      if (status && status >= 500) {
        toast.critical(
          body.status === 'rejected'
            ? t(`${prefix}.feedback.rejectFailed`)
            : t(`${prefix}.feedback.dispatchFailed`)
        )

        return
      }

      toast.critical(t(`${prefix}.feedback.genericError`))
    }
  })

  const requestApproval = useCallback(() => {
    setContentModal({
      titleModal: t(`${prefix}.approveModal.title`),
      titleAlertMessage: t(`${prefix}.approveModal.alertTitle`),
      alertMessage: t(`${prefix}.approveModal.alert`),
      alertVariant: 'warning',
      textButton: t(`${prefix}.approve`),
      templateButton: 'primary',
      status: 'approved',
      action: 'review',
      hasCancelButton: true
    })

    toggleConfirmationModal()
  }, [toggleConfirmationModal])

  const requestRejection = useCallback(() => {
    setContentModal({
      titleModal: t(`${prefix}.rejectModal.title`),
      labelMessage: t(`${prefix}.rejectModal.label`),
      subtitletLabelMessage: t(`${prefix}.rejectModal.hint`),
      textButton: t(`${prefix}.reject`),
      templateButton: 'critical',
      status: 'rejected',
      action: 'review',
      noteIsRequired: true,
      noteMaxLength: NOTE_MAX_LENGTH,
      hasCancelButton: true
    })

    toggleConfirmationModal()
  }, [toggleConfirmationModal])

  const handleSubmit = useCallback(
    (dataForm: {
      status: CampaignStatus
      action: CampaignActions
      note?: string
    }) => {
      /* O botão do modal já fica travado durante o envio; isto cobre o que não
         passa por ele, como submit por teclado antes do novo render. */
      if (isPending) return

      const note = dataForm.note?.trim()

      if (dataForm.status === 'rejected' && !note) {
        toast.critical(t(`${prefix}.feedback.noteRequired`))

        return
      }

      if (note && note.length > NOTE_MAX_LENGTH) {
        toast.critical(t(`${prefix}.feedback.noteTooLong`))

        return
      }

      mutate({
        status: dataForm.status,
        action: dataForm.action,
        ...(note ? { note } : {})
      })
    },
    [isPending, mutate, toast]
  )

  return {
    Modal,
    contentModal,
    showConfirmationModal,
    toggleConfirmationModal,
    handleSubmit,
    requestApproval,
    requestRejection,
    isPending,
    domainBlockMessage
  }
}
